import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { config } from '@/config';

const LS_KEY_COUNT = 'marketing_popup_shown_count';

interface PopupRuntimeConfig {
  delayMs?: number;
  maxShown?: number;
}

function getShownCount(): number {
  try {
    const raw = localStorage.getItem(LS_KEY_COUNT);
    if (raw === null) return 0;
    const parsed = parseInt(raw, 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  } catch {
    return 0;
  }
}

function incrementShownCount(): void {
  try {
    const current = getShownCount();
    localStorage.setItem(LS_KEY_COUNT, String(current + 1));
  } catch {
    // localStorage unavailable — ignore
  }
}

function isVideo(filename: string): boolean {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  return ext === 'mp4' || ext === 'mov';
}

/**
 * Fetch runtime popup config from the server (popup-config.json in the popups folder).
 * If the file doesn't exist or is malformed, returns null so build-time defaults are used.
 */
async function fetchRuntimeConfig(baseUrl: string): Promise<PopupRuntimeConfig | null> {
  try {
    const normalized = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
    const res = await fetch(`${normalized}popup-config.json`, { cache: 'no-store' });
    if (!res.ok) return null;
    const json: unknown = await res.json();
    if (!json || typeof json !== 'object') return null;
    const cfg = json as Record<string, unknown>;
    return {
      delayMs: typeof cfg.delayMs === 'number' && cfg.delayMs >= 0 ? cfg.delayMs : undefined,
      maxShown: typeof cfg.maxShown === 'number' && cfg.maxShown >= 0 ? cfg.maxShown : undefined,
    };
  } catch {
    return null;
  }
}

export default function MarketingPopup() {
  const [mediaFile, setMediaFile] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Hold runtime overrides in a ref so the effect closure always sees the latest
  const runtimeCfgRef = useRef<PopupRuntimeConfig>();

  const close = useCallback(() => {
    setIsOpen(false);
    incrementShownCount();
  }, []);

  // Fetch popup media and runtime config
  useEffect(() => {
    let cancelled = false;
    let showTimer: ReturnType<typeof setTimeout> | null = null;

    const listUrl = config.popup?.listUrl;
    const baseUrl = config.popup?.url;

    if (!listUrl || !baseUrl) {
      setIsLoading(false);
      return;
    }

    // Fetch runtime config AND media listing in parallel
    Promise.all([
      fetchRuntimeConfig(baseUrl),
      fetch(listUrl, { method: 'GET', cache: 'no-store' }).then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<string[]>;
      }),
    ])
      .then(([runtimeCfg, filenames]) => {
        if (cancelled) return;

        // Merge runtime config with build-time defaults
        runtimeCfgRef.current = runtimeCfg || {};

        if (!Array.isArray(filenames) || filenames.length === 0) {
          setIsLoading(false);
          return;
        }

        const effectiveMaxShown = runtimeCfgRef.current.maxShown ?? config.popup.maxShown;

        // Don't bother if already shown max times
        if (getShownCount() >= effectiveMaxShown) {
          setIsLoading(false);
          return;
        }

        // Take the first file only
        const filename = filenames[0];
        const normalizedBase = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
        const fullUrl = `${normalizedBase}${filename}`;

        setMediaFile(fullUrl);
        setIsLoading(false);

        const effectiveDelay = runtimeCfgRef.current.delayMs ?? config.popup.delayMs;

        showTimer = setTimeout(() => {
          if (!cancelled) {
            setIsOpen(true);
          }
        }, effectiveDelay);
      })
      .catch(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
      if (showTimer !== null) clearTimeout(showTimer);
    };
  }, []);

  // Keyboard: Escape to close
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, close]);

  // Nothing to show
  if (isLoading || !mediaFile || !isOpen) return null;

  const isVideoFile = isVideo(mediaFile);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 z-[200] flex items-center justify-center p-4 md:p-8"
        onClick={close}
      >
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/70" />

        {/* Popup container — transparent, media fills 75vw */}
        <motion.div
          initial={{ opacity: 0, scale: 0.85, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.85, y: 30 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          onClick={(e) => e.stopPropagation()}
          className="relative flex items-center justify-center"
          style={{ width: '75vw' }}
        >
          {/* Close button */}
          <button
            onClick={close}
            className="absolute -top-12 right-0 z-10 w-9 h-9 flex items-center justify-center bg-foreground-950/70 hover:bg-foreground-950/90 text-background-50 rounded-full transition-colors cursor-pointer"
            aria-label="Close popup"
          >
            <i className="ri-close-line text-lg"></i>
          </button>

          {/* Media content */}
          {hasError ? (
            <div className="flex items-center justify-center w-full min-h-[200px] text-background-200 text-sm bg-foreground-950/50 rounded-lg">
              Failed to load media.
            </div>
          ) : isVideoFile ? (
            <video
              src={mediaFile}
              controls
              autoPlay
              muted
              playsInline
              onError={() => setHasError(true)}
              className="w-full h-auto max-h-[80vh] object-contain"
            />
          ) : (
            <img
              src={mediaFile}
              alt="Special offer"
              onError={() => setHasError(true)}
              className="w-full h-auto max-h-[80vh] object-contain"
            />
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}